import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pianiAPI } from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ConfirmModal from '../components/ConfirmModal';

interface Piano {
  id: string;
  nome: string;
  slug: string;
  descrizione?: string;
  prezzo_mensile: number;
  prezzo_annuale?: number;
  max_utenti: number;
  max_offerte_mese: number;
  max_storage_mb: number;
  max_sync_daemons: number;
  feat_fic_integration: boolean;
  feat_digital_signature: boolean;
  feat_custom_branding: boolean;
  feat_api_access: boolean;
  feat_priority_support: boolean;
  feat_whatsapp_bridge: boolean;
  max_whatsapp_bridges: number;
  feat_ai_custom_key: boolean;
  ordine: number;
  colore: string;
  is_active: boolean;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

interface PianoFormData {
  nome: string;
  slug: string;
  descrizione?: string;
  prezzo_mensile: number;
  prezzo_annuale?: number;
  max_utenti: number;
  max_offerte_mese: number;
  max_storage_mb: number;
  max_sync_daemons: number;
  feat_fic_integration: boolean;
  feat_digital_signature: boolean;
  feat_custom_branding: boolean;
  feat_api_access: boolean;
  feat_priority_support: boolean;
  feat_whatsapp_bridge: boolean;
  max_whatsapp_bridges: number;
  feat_ai_custom_key: boolean;
  ordine: number;
  colore: string;
  is_active: boolean;
  is_public: boolean;
}

const Piani = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingPiano, setEditingPiano] = useState<Piano | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; nome: string } | null>(null);

  // Check if user is host
  if (!user?.is_host) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Accesso negato. Solo gli host users possono gestire i piani.</p>
        </div>
      </div>
    );
  }

  // Fetch piani
  const { data, isLoading, error } = useQuery({
    queryKey: ['piani', page, search],
    queryFn: async () => {
      const response = await pianiAPI.getPiani({
        page,
        limit: 20,
        search: search || undefined,
      });
      return response.data;
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: any) => pianiAPI.createPiano(data),
    onSuccess: () => {
      toast.success('Piano creato con successo');
      queryClient.invalidateQueries({ queryKey: ['piani'] });
      setShowFormModal(false);
      setEditingPiano(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante la creazione del piano');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => pianiAPI.updatePiano(id, data),
    onSuccess: () => {
      toast.success('Piano aggiornato con successo');
      queryClient.invalidateQueries({ queryKey: ['piani'] });
      setShowFormModal(false);
      setEditingPiano(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante l\'aggiornamento del piano');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => pianiAPI.deletePiano(id),
    onSuccess: () => {
      toast.success('Piano eliminato con successo');
      queryClient.invalidateQueries({ queryKey: ['piani'] });
    },
    onError: () => {
      toast.error('Errore durante l\'eliminazione del piano');
    },
  });

  const handleDelete = (id: string, nome: string) => {
    setDeleteConfirm({ id, nome });
  };

  const handleEdit = (piano: Piano) => {
    setEditingPiano(piano);
    setShowFormModal(true);
  };

  const handleCreate = () => {
    setEditingPiano(null);
    setShowFormModal(true);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestione Piani</h1>
            <p className="text-gray-600 mt-1">Gestisci i piani di sottoscrizione della piattaforma</p>
          </div>
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nuovo Piano
          </button>
        </div>

        {/* Search */}
        <div className="flex gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Cerca per nome o slug..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Piani List */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Errore nel caricamento dei piani</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Piano
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prezzo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Limiti
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Features
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stato
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.data.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    Nessun piano trovato
                  </td>
                </tr>
              ) : (
                data?.data.map((piano: Piano) => (
                  <tr key={piano.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: piano.colore }}></div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{piano.nome}</div>
                          <div className="text-xs text-gray-400">/{piano.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div>€{piano.prezzo_mensile}/mese</div>
                      {piano.prezzo_annuale && (
                        <div className="text-xs text-gray-400">€{piano.prezzo_annuale}/anno</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div>{piano.max_utenti} utenti</div>
                      <div>{piano.max_offerte_mese} offerte/mese</div>
                      <div className="text-xs text-gray-400">{piano.max_storage_mb} MB storage</div>
                      <div className="text-xs text-gray-400">{piano.max_sync_daemons} daemon sync</div>
                      {piano.feat_whatsapp_bridge && <div className="text-xs text-green-700">WhatsApp: max {piano.max_whatsapp_bridges} bridge</div>}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {piano.feat_fic_integration && (
                          <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">FIC</span>
                        )}
                        {piano.feat_digital_signature && (
                          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">Firma</span>
                        )}
                        {piano.feat_custom_branding && (
                          <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">Branding</span>
                        )}
                        {piano.feat_api_access && (
                          <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded">API</span>
                        )}
                        {piano.feat_priority_support && (
                          <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded">Support</span>
                        )}
                        {piano.feat_ai_custom_key && (
                          <span className="px-2 py-1 text-xs bg-indigo-100 text-indigo-800 rounded">AI Key</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full inline-block w-fit ${
                          piano.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {piano.is_active ? 'Attivo' : 'Inattivo'}
                        </span>
                        {piano.is_public && (
                          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full inline-block w-fit">
                            Pubblico
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEdit(piano)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Modifica"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(piano.id, piano.nome)}
                          className="text-red-600 hover:text-red-900"
                          title="Elimina"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {data?.pagination && data.pagination.pages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Precedente
                </button>
                <button
                  onClick={() => setPage(Math.min(data.pagination.pages, page + 1))}
                  disabled={page === data.pagination.pages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Successivo
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Pagina <span className="font-medium">{page}</span> di{' '}
                    <span className="font-medium">{data.pagination.pages}</span>
                    {' '}({data.pagination.total} piani totali)
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setPage(Math.min(data.pagination.pages, page + 1))}
                      disabled={page === data.pagination.pages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Form Modal */}
      {showFormModal && (
        <PianoFormModal
          piano={editingPiano}
          onSubmit={(data) => {
            if (editingPiano) {
              updateMutation.mutate({ id: editingPiano.id, data });
            } else {
              createMutation.mutate(data);
            }
          }}
          onClose={() => {
            setShowFormModal(false);
            setEditingPiano(null);
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => {
          if (deleteConfirm) {
            deleteMutation.mutate(deleteConfirm.id);
            setDeleteConfirm(null);
          }
        }}
        title="Conferma Eliminazione"
        message={`Sei sicuro di voler eliminare il piano "${deleteConfirm?.nome}"?`}
        confirmLabel="Elimina"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};

// Piano Form Modal Component
interface PianoFormModalProps {
  piano?: Piano | null;
  onSubmit: (data: PianoFormData) => void;
  onClose: () => void;
  isLoading?: boolean;
}

const PianoFormModal: React.FC<PianoFormModalProps> = ({ piano, onSubmit, onClose, isLoading }) => {
  const { register, handleSubmit, formState: { errors } } = useForm<PianoFormData>({
    defaultValues: piano || {
      nome: '',
      slug: '',
      descrizione: '',
      prezzo_mensile: 0,
      prezzo_annuale: undefined,
      max_utenti: 1,
      max_offerte_mese: 5,
      max_storage_mb: 100,
      max_sync_daemons: 1,
      feat_fic_integration: false,
      feat_digital_signature: false,
      feat_custom_branding: false,
      feat_api_access: false,
      feat_priority_support: false,
      feat_whatsapp_bridge: false,
      max_whatsapp_bridges: 1,
      feat_ai_custom_key: false,
      ordine: 0,
      colore: '#2563eb',
      is_active: true,
      is_public: true,
    }
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {piano ? 'Modifica Piano' : 'Nuovo Piano'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Info */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informazioni Base</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome Piano *</label>
                <input
                  type="text"
                  {...register('nome', { required: 'Nome richiesto' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.nome && <p className="text-red-600 text-sm mt-1">{errors.nome.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
                <input
                  type="text"
                  {...register('slug', {
                    required: 'Slug richiesto',
                    pattern: { value: /^[a-z0-9-]+$/, message: 'Solo minuscole, numeri e trattini' }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="piano-pro"
                />
                {errors.slug && <p className="text-red-600 text-sm mt-1">{errors.slug.message}</p>}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
                <textarea
                  {...register('descrizione')}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Prezzi</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo Mensile (€) *</label>
                <input
                  type="number"
                  step="0.01"
                  {...register('prezzo_mensile', {
                    required: 'Prezzo mensile richiesto',
                    min: { value: 0, message: 'Minimo 0' }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.prezzo_mensile && <p className="text-red-600 text-sm mt-1">{errors.prezzo_mensile.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo Annuale (€)</label>
                <input
                  type="number"
                  step="0.01"
                  {...register('prezzo_annuale', { min: { value: 0, message: 'Minimo 0' } })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Limits */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Limiti</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Utenti *</label>
                <input
                  type="number"
                  {...register('max_utenti', {
                    required: 'Max utenti richiesto',
                    min: { value: 1, message: 'Minimo 1' }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.max_utenti && <p className="text-red-600 text-sm mt-1">{errors.max_utenti.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Offerte/Mese *</label>
                <input
                  type="number"
                  {...register('max_offerte_mese', {
                    required: 'Max offerte richiesto',
                    min: { value: 1, message: 'Minimo 1' }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.max_offerte_mese && <p className="text-red-600 text-sm mt-1">{errors.max_offerte_mese.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Storage (MB) *</label>
                <input
                  type="number"
                  {...register('max_storage_mb', {
                    required: 'Max storage richiesto',
                    min: { value: 1, message: 'Minimo 1' }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.max_storage_mb && <p className="text-red-600 text-sm mt-1">{errors.max_storage_mb.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Sync Daemon *</label>
                <input
                  type="number"
                  {...register('max_sync_daemons', {
                    required: 'Max daemon richiesto',
                    min: { value: 1, message: 'Minimo 1' }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.max_sync_daemons && <p className="text-red-600 text-sm mt-1">{errors.max_sync_daemons.message}</p>}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2"><input type="checkbox" {...register('feat_whatsapp_bridge')} />Bridge WhatsApp</label>
            <label className="text-sm">Massimo bridge WhatsApp <input type="number" className="ml-2 w-24 rounded-md border-gray-300" {...register('max_whatsapp_bridges', { valueAsNumber: true, required: true, min: 0, max: 100 })} /></label>
            {errors.max_whatsapp_bridges && <p className="text-sm text-red-600">Inserisci un limite tra 0 e 100.</p>}
          </div>
          {/* Features */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Features</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" {...register('feat_fic_integration')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                <span className="text-sm font-medium text-gray-700">Integrazione FIC</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" {...register('feat_digital_signature')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                <span className="text-sm font-medium text-gray-700">Firma Digitale</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" {...register('feat_custom_branding')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                <span className="text-sm font-medium text-gray-700">Branding Personalizzato</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" {...register('feat_api_access')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                <span className="text-sm font-medium text-gray-700">Accesso API</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" {...register('feat_priority_support')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                <span className="text-sm font-medium text-gray-700">Supporto Prioritario</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" {...register('feat_ai_custom_key')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                <span className="text-sm font-medium text-gray-700">Chiave API AI gestita dal tenant</span>
              </label>
            </div>
          </div>

          {/* Display */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Visualizzazione</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ordine *</label>
                <input
                  type="number"
                  {...register('ordine', {
                    required: 'Ordine richiesto',
                    min: { value: 0, message: 'Minimo 0' }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.ordine && <p className="text-red-600 text-sm mt-1">{errors.ordine.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Colore Badge</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    {...register('colore')}
                    className="h-10 w-16 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    {...register('colore', { pattern: { value: /^#[0-9A-Fa-f]{6}$/, message: 'Colore non valido' } })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="#2563eb"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Status */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Stato</h3>
            <div className="flex gap-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" {...register('is_active')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                <span className="text-sm font-medium text-gray-700">Piano Attivo</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" {...register('is_public')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                <span className="text-sm font-medium text-gray-700">Visibile Pubblicamente</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              {piano ? 'Salva Modifiche' : 'Crea Piano'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Piani;
